import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { KTSVG } from '../../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import * as XLSX from 'xlsx'
import html2pdf from 'html2pdf.js'

import SignOnModal from './SignOnModal'
import {
  getCrewList,
  getRanks,
  getSignOnOffRecords,
  getRanksforList,
  type SignOnOffRecord,
} from '../core/_requests'
import { Crew, Rank, Vessel } from '../core/_models'
import SignOffModal from './SignOffModal'
import ViewAssignmentModal from './ViewAssignmentModal'
import EditAssignmentModal from './EditAssignmentModal'
import HistoryModal from './HistoryModal'
import { useAuth } from '../../auth'
import { CREWING_ACTION_PERMISSIONS, CREWING_READ_PERMISSIONS, useCrewingPermissionFetch, useCrewingPermissionRevision } from '../core/crewingPermissionAccess'
import { extractRows } from '../core/crewingPresentationFilters'
import { useCrewingRoleAccess } from '../core/crewingRoleAccess'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'

/** format “YYYY-MM-DD” (or ISO) → “DD-MMM-YYYY” e.g. “12-Jul-2025” */
// allow string|null|undefined
const formatDate = (iso?: string | null) => {
  // 1) no value
  if (!iso) {
    return '—'
  }
  const d = new Date(iso)
  // 2) invalid date
  if (isNaN(d.getTime())) {
    return '—'
  }
  const dd = String(d.getDate()).padStart(2, '0')
  const mmm = d.toLocaleString('en-US', { month: 'short' })
  const yyyy = d.getFullYear()
  return `${dd}-${mmm}-${yyyy}`
}

// --- Rank sorting helpers (reuse from Sign page) ---
const RANK_ORDER = [
  'MASTER', 'CHIEF OFFICER', 'SECOND OFFICER', 'THIRD OFFICER', 'DECK CADET',
  'CHIEF ENGINEER', 'SECOND ENGINEER', 'THIRD ENGINEER', 'FOURTH ENGINEER',
  'TRAINEE MARINE ENGINEER', 'ELECTRICAL OFFICER', 'BOSUN', 'PUMPMAN',
  'ABLE SEAMAN', 'ORDINARY SEAMAN', 'TR. SEAMAN', 'OILER', 'WIPER', 'TR WIPER',
  'FITTER', 'CHIEF COOK', 'GENERAL STEWARD',
] as const;

const RANK_REGEX: Record<string, RegExp[]> = {
  MASTER: [/(^|\s)master\b/i],
  'CHIEF OFFICER': [/chief\s*officer/i, /\bc\s*\/\s*o\b/i],
  'SECOND OFFICER': [/second\s*officer/i, /\b2\s*\/\s*o\b/i],
  'THIRD OFFICER': [/third\s*officer/i, /\b3\s*\/\s*o\b/i],
  'DECK CADET': [/deck\s*cadet/i],
  'CHIEF ENGINEER': [/chief\s*engineer/i, /\bc\s*\/\s*e\b/i],
  'SECOND ENGINEER': [/second\s*engineer/i, /\b2\s*\/\s*e\b/i],
  'THIRD ENGINEER': [/third\s*engineer/i, /\b3\s*\/\s*e\b/i],
  'FOURTH ENGINEER': [/fourth\s*engineer/i, /\b4\s*\/\s*e\b/i, /fouth\s*engineer/i],
  'TRAINEE MARINE ENGINEER': [/engine\s*cadet/i, /trainee\s*marine\s*engineer/i, /\btme\b/i],
  'ELECTRICAL OFFICER': [/electrician/i, /electrical/i, /\beto\b/i],
  BOSUN: [/\bbosun\b/i, /boatswain/i],
  PUMPMAN: [/pumpman/i],
  'ABLE SEAMAN': [/able\s*seaman/i, /\bab\b(?![a-z])/i],
  'ORDINARY SEAMAN': [/ordinary\s*seaman/i, /\bos\b(?![a-z])/i],
  'TR. SEAMAN': [/trainee.*seaman/i, /\btr\.?\s*seaman\b/i],
  OILER: [/oiler/i, /motorman/i],
  WIPER: [/wiper\b/i],
  'TR WIPER': [/trainee.*wiper/i, /\btr\.?\s*wiper\b/i],
  FITTER: [/fitter\b/i],
  'CHIEF COOK': [/chief\s*cook/i],
  'GENERAL STEWARD': [/steward/i, /messman/i],
};

const getCanonicalRank = (label?: string): string => {
  const s = (label ?? '').trim();
  for (const [canon, patterns] of Object.entries(RANK_REGEX)) {
    if (patterns.some(rx => rx.test(s))) return canon;
  }
  return 'OTHER';
};

const getRankSuffix = (label?: string): number => {
  const s = (label ?? '').trim();
  const m = s.match(/-\s*(\d+)\s*$/);
  return m ? Number(m[1]) : 0; // 0 => base
};

const rankTuple = (label?: string): [number, number, number, string] => {
  const canon = getCanonicalRank(label);
  const groupIdx = canon === 'OTHER' ? 999 : RANK_ORDER.indexOf(canon as any) + 1;
  const suffix = getRankSuffix(label);
  const baseFirstFlag = suffix === 0 ? 0 : 1;
  return [groupIdx, baseFirstFlag, suffix, (label ?? '')];
};

const compareRank = (a?: string, b?: string, dir: 'asc' | 'desc' = 'asc'): number => {
  const A = rankTuple(a);
  const B = rankTuple(b);
  const cmp = (A[0] - B[0]) || (A[1] - B[1]) || (A[2] - B[2]) || A[3].localeCompare(B[3]);
  return dir === 'asc' ? cmp : -cmp;
};


const AssignmentPage: FC = () => {
  const { state: globalState, vessels: globalVessels } = useGlobalFilter()

  // Sync global header search query → local searchTerm
  useEffect(() => {
    setSearchTerm(globalState.searchQuery || '')
  }, [globalState.searchQuery])

  // Role resolution & operator flavors
  const { currentUser, hasPermission, hasAnyPermission } = useAuth()
  const {
    isSuperAdmin,
    isCrew,
    isCompanyGroupAdmin,
    isCompanyAdmin,
    isOperator: baseIsOperator,
    isOfficeRole,
  } = useCrewingRoleAccess()
  const roleId: number = currentUser?.role?.id ?? 0
  const roleEntityId = currentUser?.roleEntityId ?? null
  const cgaId = currentUser?.companyGroupAdminId ?? (currentUser as any)?.companyGroupAdmin?.id ?? null
  const operatorCompanyAdminId = currentUser?.companyAdminId ?? (currentUser as any)?.companyAdmin?.id ?? null
  const operatorCompanyGroupAdminId = currentUser?.companyGroupAdminId ?? (currentUser as any)?.companyGroupAdmin?.id ?? null

  const isOperator = baseIsOperator || roleId === 6
  const operatorActsLikeCompanyAdmin = isOperator && operatorCompanyAdminId != null
  const operatorActsLikeGroupAdmin = isOperator && !operatorActsLikeCompanyAdmin && operatorCompanyGroupAdminId != null
  const operatorActsLikeSuperadmin = isOperator && !operatorActsLikeCompanyAdmin && operatorCompanyGroupAdminId == null

  const [isSignOnModalOpen, setIsSignOnModalOpen] = useState<boolean>(false) // ← renamed

  const [records, setRecords] = useState<SignOnOffRecord[]>([])
  const [isViewAssignmentModalOpen, setIsViewAssignmentModalOpen] = useState(false)
  const [isEditAssignmentModalOpen, setIsEditAssignmentModalOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<SignOnOffRecord | null>(null)

  const [crew, setCrew] = useState<Crew[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [vesselList, setVesselList] = useState<string[]>([])
  const selectedVesselId = globalState.vessel?.id || ((isCrew || roleId === 4) ? currentUser?.vessel?.id : null) || null
  const selectedVessel = globalState.vessel?.fleet_name || ((isCrew || roleId === 4) ? currentUser?.vessel?.fleet_name : null) || 'all'
  const isSpecificVesselSelected = Boolean(selectedVessel && selectedVessel !== 'all')

  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const indexOfLastCompany = currentPage * rowsPerPage
  const indexOfFirstCompany = indexOfLastCompany - rowsPerPage

  const [showCrewCreds, setShowCrewCreds] = useState(false)
  const [newCrewPassword, setNewCrewPassword] = useState<string | null>(null)
  const [crewLoginLink, setCrewLoginLink] = useState<string | null>(null)
  const [ranks, setRanks] = useState<Rank[]>([])
  const [rankMap, setRankMap] = useState<Record<number, string>>({})

  const [hoveredSignOffId, setHoveredSignOffId] = useState<number | null>(null)

  const [isSignOffModalOpen, setIsSignOffModalOpen] = useState(false)
  const [signOffRecordId, setSignOffRecordId] = useState<number | null>(null)
  const [recordToSignOff, setRecordToSignOff] = useState<SignOnOffRecord | null>(null)
  const [signOnActive, setSignOnActive] = useState(false)
  const [showIMOExportModal, setShowIMOExportModal] = useState<boolean>(false)
  const [showVesselFilterAlert, setShowVesselFilterAlert] = useState<boolean>(false)
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false)
  const exportDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        exportDropdownRef.current &&
        !exportDropdownRef.current.contains(event.target as Node)
      ) {
        setIsExportDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const [statusFilter, setStatusFilter] = useState<string>('All')
  const [loading, setLoading] = useState<boolean>(true)

  // Sorting
  const [sortColumn, setSortColumn] = useState<string>('rank')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const [signOnLink, setSignOnLink] = useState<string | null>(null)
  const [showSignOnLink, setShowSignOnLink] = useState(false)

  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false)
  const [historyRecordId, setHistoryRecordId] = useState<number | null>(null)
  const [historyCrewName, setHistoryCrewName] = useState<string>('')
  const userRankId = currentUser?.rank?.id
  const userRankName = currentUser?.rank?.rank
  const isMasterCrewUser =
    (isCrew || roleId === 4) && (userRankId === 1 || userRankName?.toLowerCase().includes('master'))

  const canExportAssignments = isSuperAdmin || hasPermission('DOWNLOAD_CREW_ASSIGNMENT')
  const canUpdateAssignment = isSuperAdmin || hasPermission('UPDATE_CREW_ASSIGNMENT')
  const canSignOn = isSuperAdmin || hasPermission('SIGN_ON_CREW_ASSIGNMENT')
  const canSignOff = isSuperAdmin || hasPermission('SIGN_OFF_CREW_ASSIGNMENT')
  const canViewAssignment = isSuperAdmin || hasPermission(CREWING_READ_PERMISSIONS.CREW_ASSIGNMENT)
  const permissionRevision = useCrewingPermissionRevision()
  const fetchCrewList = useCallback(async () => getCrewList(), [])
  const resolveRecordVesselId = (record: SignOnOffRecord) => record.vesselId ?? null
  useEffect(() => {
    if (!canViewAssignment) {
      setRecords([])
      setLoading(false)
      return
    }
    setLoading(true)
    // 1) ranks
    const ranksPromise = getRanksforList()
      .then((rs) => {
        const m: Record<number, string> = {}
        rs.forEach((r) => (m[r.id] = r.rank))
        setRanks(rs)
        setRankMap(m)
      })
      .catch((err) => {
        console.error('Failed to fetch ranks', err)
        showErrorToast(err, 'Failed to load rank master list.', 'load')
      })

    // 2) vessels — backend returns RBAC-scoped list
    const recordsPromise = fetchAccessibleVesselsForCurrentUser()
      .then((vs) => {
        const active = normalizeAccessibleVessels(vs).filter((v) => v.active)
        setVessels(active)
        setVesselList(active.map((v) => v.fleet_name))

        return getSignOnOffRecords()
          .then((all) => {
            setRecords(extractRows<SignOnOffRecord>(all))
          })
          .catch((err) => {
            console.error('Failed to fetch records', err)
            showErrorToast(err, 'Failed to load assignment records.', 'load')
          })
      })
      .catch((err) => {
        console.error(err)
        showErrorToast(err, 'Failed to load accessible vessels.', 'load')
      })

    Promise.allSettled([ranksPromise, recordsPromise]).finally(() => {
      setLoading(false)
    })
  }, [canViewAssignment, permissionRevision])


  useCrewingPermissionFetch({
    permissionCode: CREWING_READ_PERMISSIONS.CREW_ASSIGNMENT,
    fetcher: fetchCrewList,
    onSuccess: setCrew,
    onDenied: () => setCrew([]),
    onError: (err) => {
      console.error('Failed to fetch crew', err)
      showErrorToast(err, 'Failed to load crew list.', 'load')
    },
  })

  useEffect(() => {
    if (crew.length > 0 && searchTerm) {
      const hasMatch = crew.some((c) => c.name?.toLowerCase().includes(searchTerm.toLowerCase()))
      if (!hasMatch) {
        setSearchTerm('') // Clear search if it causes empty view
      }
    }
  }, [crew])

  const handleExcelDownload = () => {
    try {
      const worksheetData = sorted.map((rec, idx) => ({
        'SR/NO': idx + 1,
        NAME: rec.crewName,
        RANK: rec.rankInfo?.rank || rankMap[rec.rank] || '-',
        VESSEL: rec.vesselName,
        'SIGN ON PORT': rec.portSignOn,
        'SIGN ON DATE': formatDate(rec.signOnDate),
        'SIGN OFF PORT': rec.portSignOff || '-',
        'SIGN OFF DATE': formatDate(rec.signOffDate),
        STATUS:
          rec.status === 'SIGNED_ON'
            ? 'SIGNED ON'
            : rec.status === 'SIGNED_OFF'
              ? 'SIGNED OFF'
              : rec.status === 'PLANNED'
                ? 'PLANNED SIGNED ON'
                : rec.status === 'PLANNED_SIGN_OFF'
                  ? 'PLANNED SIGNED OFF'
                  : rec.status || 'Unknown',
      }))

      const worksheet = XLSX.utils.json_to_sheet(worksheetData)
      const workbook = XLSX.utils.book_new()
      worksheet['!cols'] = [
        { wch: 8 },
        { wch: 24 },
        { wch: 22 },
        { wch: 22 },
        { wch: 18 },
        { wch: 16 },
        { wch: 18 },
        { wch: 16 },
        { wch: 20 },
      ]
      XLSX.utils.book_append_sheet(workbook, worksheet, 'CrewAssignment')
      XLSX.writeFile(workbook, 'crewassignments.xlsx')
    } catch (err) {
      console.error(err)
      showErrorToast(err, 'Failed to export assignment Excel report.', 'general')
    }
  }

  const handleExportIMO = () => {
    if (!isSpecificVesselSelected) {
      setShowVesselFilterAlert(true)
      return
    }

    const element = document.getElementById('imo-pdf-content-assignment')
    if (!element) return

    element.style.display = 'block'

    const opt = {
      margin: [0.3, 0.3, 0.6, 0.3],
      filename: `IMO_Crew_List_${selectedVessel}.pdf`,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 2, useCORS: true, allowTaint: true, logging: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'], avoid: ['tr', '.signature-box'] }
    }

    html2pdf()
      .set(opt)
      .from(element)
      .toPdf()
      .get('pdf')
      .then((pdf: any) => {
        const pageCount = pdf.internal.getNumberOfPages()
        pdf.setFontSize(10)
        for (let i = 1; i <= pageCount; i++) {
          pdf.setPage(i)
          const w = pdf.internal.pageSize.getWidth()
          const h = pdf.internal.pageSize.getHeight()

          // 👇 Changed h - 0.5 to h - 0.3 👇
          pdf.text(`Page ${i} of ${pageCount}`, w - 0.5, h - 0.3, { align: 'right' })
        }
      })
      .save()
      .catch((err: any) => {
        console.error(err)
        showErrorToast(err, 'Failed to export IMO Crew List PDF.', 'general')
      })
      .finally(() => {
        element.style.display = 'none'
      })
  }

  const resolveCrewForRecord = (record: SignOnOffRecord) =>
    crew.find((candidate) => candidate.id === record.crewId)

  const normalizeText = (value?: string | null) => (value ? value.trim() : '')
  const selectedVesselName = normalizeText(selectedVessel)
  const selectedVesselRecord = vessels.find(
    (vessel) =>
      (selectedVesselId && Number(vessel.id) === Number(selectedVesselId)) ||
      normalizeText(vessel.fleet_name) === selectedVesselName
  )
  const selectedVesselCrew = crew.find(
    (record) =>
      (selectedVesselId && (Number(record.vessel?.id) === Number(selectedVesselId) || Number(record.vesselId) === Number(selectedVesselId))) ||
      normalizeText(record.vesselName) === selectedVesselName
  )

  // Handle filter change
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value)
  }

  const handleSort = (column: string) => {
    // ← new
    if (sortColumn === column) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const handleSignOn = async (data: any) => {
    setIsSignOnModalOpen(false)
    try {
      // re-fetch the full list, so every record has crewName, vesselName, rank, etc.
      const all = await getSignOnOffRecords()
      setRecords(all)
    } catch (err) {
      console.error(err)
      showErrorToast(err, 'Failed to refresh assignments.', 'load')
    }
  }

  const handleViewClick = (record: SignOnOffRecord) => {
    setSelectedRecord(record)
    setIsViewAssignmentModalOpen(true)
  }

  const handleEditClick = (record: SignOnOffRecord) => {
    if (isMasterCrewUser) {
      return
    }

    if (
      record.status === 'PLANNED' ||
      record.status === 'SIGNED_ON' ||
      record.status === 'PLANNED_SIGN_OFF'
    ) {
      setSelectedRecord(record)
      setIsEditAssignmentModalOpen(true) // Open the modal for editing
    }
  }

  const handleUpdate = (updatedRecord: SignOnOffRecord) => {
    // Update the record in the list after editing
    setRecords((prevRecords) =>
      prevRecords.map((rec) => (rec.id === updatedRecord.id ? updatedRecord : rec))
    )
    setIsEditAssignmentModalOpen(false)
  }

  // Combined filtering + sorting
  const filtered = records.filter((rec) => {
    // search within crew name
    const matchesSearch = rec.crewName.toLowerCase().includes(searchTerm.toLowerCase())
    // status
    const matchesStatus = statusFilter === 'All' || rec.status === statusFilter
    // global vessel filter
    let matchesVessel = true
    if (globalState.vessel?.id) {
      const recVesselId = resolveRecordVesselId(rec)
      matchesVessel = recVesselId != null ? Number(recVesselId) === Number(globalState.vessel.id) : rec.vesselName === globalState.vessel.fleet_name
    } else if (globalState.company) {
      const allowedVesselIds = new Set(globalVessels.map(v => Number(v.id)))
      const allowedNames = new Set(globalVessels.map(v => v.fleet_name))
      const recVesselId = resolveRecordVesselId(rec)
      if (recVesselId) matchesVessel = allowedVesselIds.has(Number(recVesselId))
      else if (rec.vesselName) matchesVessel = allowedNames.has(rec.vesselName)
    }
    return matchesSearch && matchesStatus && matchesVessel
  })

  const sorted = [...filtered].sort((a, b) => {
    // Default: newest-first by id if no explicit sortColumn
    if (!sortColumn) return b.id - a.id;

    // Rank column → use canonical maritime order (rankMap maps id -> rank name)
    if (sortColumn === 'rank') {
      const ra = a.rankInfo?.rank || rankMap[a.rank] || '';
      const rb = b.rankInfo?.rank || rankMap[b.rank] || '';
      return compareRank(ra, rb, sortOrder);
    }

    // Date columns
    if (sortColumn === 'signOnDate' || sortColumn === 'signOffDate') {
      const da = new Date((a as any)[sortColumn] ?? '');
      const db = new Date((b as any)[sortColumn] ?? '');
      const diff = da.getTime() - db.getTime();
      return sortOrder === 'asc' ? diff : -diff;
    }

    // Text/other columns
    const av = String((a as any)[sortColumn] ?? '');
    const bv = String((b as any)[sortColumn] ?? '');
    return sortOrder === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });


  const totalPages = Math.ceil(sorted.length / rowsPerPage)
  const current = sorted.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
  const vesselRecords = records.filter((rec) => {
    if (!isSpecificVesselSelected) return false
    const recVesselId = resolveRecordVesselId(rec)
    if (selectedVesselId && recVesselId) {
      return Number(recVesselId) === Number(selectedVesselId) && rec.status !== 'SIGNED_OFF'
    }
    return rec.vesselName === selectedVessel && rec.status !== 'SIGNED_OFF'
  })

  // 1️⃣ Detect if any filter is active
  const isAnyFilterActive =
    searchTerm.trim() !== '' || statusFilter !== 'All'

  // 2️⃣ Handler to reset them all
  const clearFilters = () => {
    setSearchTerm('')
    setStatusFilter('All')
    setCurrentPage(1)
  }

  const handleHistoryClick = (rec: SignOnOffRecord) => {
    setHistoryRecordId(rec.id)
    setHistoryCrewName(rec.crewName)
    setIsHistoryModalOpen(true)
  }

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div
          id='kt_app_content'
          className='app-content flex-column-fluid d-flex flex-column '
          style={{ flex: 1 }}
        >
          <div className='card flex-column-fluid d-flex flex-column border-top' style={{ flex: 1, background: '#ffffff' }}>
            {/* Header */}
            <div className='d-flex gap-3 pe-5 ps-5'>
              <div className='py-3 ms-3' style={{ flex: 5 }}>
                <h3 className='card-title fw-bold text-dark mb-5'>Crew Assignment</h3>
                <div className='d-flex justify-content-between mb-5'>
                  <div className='d-flex align-items-center gap-2'>
                    <input
                      type='text'
                      className='form-control cp_search_input'
                      placeholder='Crew Name'
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ width: '220px' }}
                    />
                    {/* Filter Dropdown */}
                    <div style={{ width: '200px', minWidth: '200px' }}>
                      <SearchableSelect
                        id='statusFilter'
                        className='form-select custom-dropdown'
                        value={statusFilter}
                        onChange={handleFilterChange}
                      >
                        <option value='All'>All assigned</option>
                        <option value='SIGNED_ON'>Signed On</option>
                        <option value='SIGNED_OFF'>Signed Off</option>
                        <option value='PLANNED'>Planned Sign On</option>
                        <option value='PLANNED_SIGN_OFF'>Planned Sign Off</option>
                      </SearchableSelect>
                    </div>

                    {/* 3️⃣ Clear Filters button */}
                    <button
                      className='btn btn-outline-secondary border border-secondary'
                      disabled={!isAnyFilterActive}
                      onClick={clearFilters}
                    >
                      Clear&nbsp;Filters
                    </button>
                  </div>
                  <div className='d-flex align-items-center gap-2'>
                    {canExportAssignments && (
                      <div
                        ref={exportDropdownRef}
                        className='position-relative d-inline-block'
                      >
                        <button
                          type='button'
                          className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                          style={{
                            backgroundColor: 'rgb(220, 38, 38)',
                            borderColor: 'rgb(220, 38, 38)',
                            borderRadius: '9.75px',
                            padding: '8px 13px',
                            fontSize: '11.5px',
                            fontWeight: 700,
                            boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                            transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                            gap: '6.5px',
                          }}
                          onClick={() => setIsExportDropdownOpen((prev) => !prev)}
                          title='Export Crew Assignment'
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='14'
                            height='14'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            style={{ width: '13px', height: '13px' }}
                          >
                            <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'></path>
                            <polyline points='7 10 12 15 17 10'></polyline>
                            <line x1='12' y1='15' x2='12' y2='3'></line>
                          </svg>
                          Export
                        </button>
                        {isExportDropdownOpen && (
                          <div
                            className='dropdown-menu show'
                            style={{
                              display: 'block',
                              position: 'absolute',
                              top: '100%',
                              right: 0,
                              left: 'auto',
                              backgroundColor: '#ffffff',
                              border: '1px solid #ddd',
                              borderRadius: '0.25rem',
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                              zIndex: 1000,
                              minWidth: '200px',
                              padding: '0.5rem 0',
                              opacity: 1,
                              visibility: 'visible',
                              transform: 'none',
                            }}
                          >
                            <div
                              className='dropdown-item'
                              style={{ padding: '0.5rem 1rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
                              onClick={() => {
                                setIsExportDropdownOpen(false)
                                if (!isSpecificVesselSelected) {
                                  setShowVesselFilterAlert(true)
                                } else {
                                  setShowIMOExportModal(true)
                                }
                              }}
                            >
                              Export IMO Format
                            </div>
                            <div
                              className='dropdown-item'
                              style={{ padding: '0.5rem 1rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
                              onClick={() => {
                                setIsExportDropdownOpen(false)
                                handleExcelDownload()
                              }}
                            >
                              Export Company Format
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {canSignOn && (
                      <button
                        className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                        style={{
                          backgroundColor: 'rgb(29, 78, 216)',
                          borderColor: 'rgb(29, 78, 216)',
                          borderRadius: '9.75px',
                          padding: '8px 13px',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                          transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                          gap: '6.5px',
                        }}
                        onClick={() => setIsSignOnModalOpen(true)}
                      >
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='14'
                          height='14'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          style={{ width: '13px', height: '13px' }}
                        >
                          <path d='M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4'></path>
                          <polyline points='10 17 15 12 10 7'></polyline>
                          <line x1='15' y1='12' x2='3' y2='12'></line>
                        </svg>
                        SIGN ON CREW
                      </button>
                    )}
                  </div>
                </div>
                {/* Summary Cards */}
                <div className='py-3'>
                  {/* Reports Table */}
                  <div className='report-table table-responsive'>
                    <table className='table table-bordered align-middle'>
                      <thead className='table-header py-5'>
                        <tr>
                          {[
                            { key: 'srNo', label: 'SR/NO', isStatic: true },
                            { key: 'crewName', label: 'NAME' },
                            { key: 'rank', label: 'RANK' },
                            { key: 'vesselName', label: 'VESSEL' },
                            { key: 'portSignOn', label: 'SIGN ON PORT' },
                            { key: 'signOnDate', label: 'SIGN ON DATE' },
                            { key: 'portSignOff', label: 'SIGN OFF PORT' },
                            { key: 'signOffDate', label: 'SIGN OFF DATE' },
                            { key: 'status', label: 'STATUS' },
                          ].map((col) => (
                            <th key={col.key}>
                              {col.label}
                              <button
                                onClick={() => handleSort(col.key)}
                                className='btn btn-link p-0 m-0 pb-1'
                                disabled={records.length === 0}
                              >
                                <KTSVG
                                  path={`/media/map/sort-col-${sortColumn === col.key
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                    }.svg`}
                                />
                              </button>
                            </th>
                          ))}
                          <th>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody className='table-body'>
                        {loading ? (
                          <TableSkeleton rows={rowsPerPage || 10} cols={10} />
                        ) : current.length === 0 ? (
                          <tr>
                            <td colSpan={10} className='text-center py-4 text-muted'>
                              No crew found
                            </td>
                          </tr>
                        ) : (
                          current.map((rec, idx) => (
                            <tr key={idx}>
                              <td className="text-nowrap">{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                              <td
                                className='clickable text-nowrap text-dark fw-bold text-hover-primary'
                                onClick={() => handleHistoryClick(rec)}
                                style={{ cursor: 'pointer' }}
                              >
                                {rec.crewName}
                              </td>
                              <td className="text-nowrap">{rec.rankInfo?.rank || rankMap[rec.rank] || '—'}</td>
                              <td className="text-nowrap">
                                <VesselLandingLink
                                  vesselId={resolveRecordVesselId(rec)}
                                  vesselName={rec.vesselName}
                                  className='text-dark fw-bold text-hover-primary'
                                />
                              </td>
                              <td className="text-nowrap">{rec.portSignOn}</td>
                              <td className="text-nowrap">{formatDate(rec.signOnDate)}</td>
                              <td className="text-nowrap">
                                {
                                  rec.portSignOff ||
                                  // <span className='badge badge-light-warning'>
                                  '—'
                                  // </span>
                                  // <span className="badge bg-warning text-dark">Not Yet Signed Off</span>
                                }
                              </td>
                              <td className="text-nowrap">
                                {
                                  formatDate(
                                    rec.signOffDate ||
                                    // <span className='badge badge-light-warning'>
                                    '—'
                                    // </span>
                                  )
                                  // <span className="badge bg-warning text-dark">Not Yet Signed Off</span>
                                }
                              </td>
                              <td className="text-nowrap">
                                {rec.status === 'SIGNED_ON' ? (
                                  <span className='badge badge-light-success '>SIGNED ON</span>
                                ) : rec.status === 'SIGNED_OFF' ? (
                                  <span className='badge badge-light-secondary '>SIGNED OFF</span>
                                ) : rec.status === 'PLANNED' ? (
                                  <span className='badge badge-light-warning '>
                                    PLANNED SIGNED ON
                                  </span>
                                ) : rec.status === 'PLANNED_SIGN_OFF' ? (
                                  <span className='badge badge-light-danger '>
                                    PLANNED SIGNED OFF
                                  </span>
                                ) : (
                                  <span className='badge bg-light-warning text-dark'>Unknown</span>
                                )}
                              </td>

                              <td className="text-nowrap">
                                {!isMasterCrewUser && canUpdateAssignment && (
                                  <button
                                    className='btn btn-sm btn-secondary btn-icon mb-2 me-2'
                                    onClick={() => handleEditClick(rec)}
                                    disabled={
                                      rec.status !== 'PLANNED' &&
                                      rec.status !== 'SIGNED_ON' &&
                                      rec.status !== 'PLANNED_SIGN_OFF'
                                    } // Disable for other statuses
                                  >
                                    <KTSVG
                                      path={
                                        rec.status === 'PLANNED' ||
                                          rec.status === 'SIGNED_ON' ||
                                          rec.status === 'PLANNED_SIGN_OFF'
                                          ? '/media/map/edit-active.svg' // active status, regular icon
                                          : '/media/map/edit-active-grey.svg' // inactive status, greyed out icon
                                      }
                                    />
                                  </button>
                                )}

                                <button
                                  className='btn btn-sm btn-secondary btn-icon mb-2 me-2'
                                  onClick={() => handleViewClick(rec)}
                                >
                                  <KTSVG path='/media/map/ph_eye.svg' className='' />
                                </button>
                                {canSignOff && (
                                  <button
                                    className={`btn btn-sm mb-2 ${rec.status === 'SIGNED_OFF' || rec.status === 'PLANNED_SIGN_OFF'
                                      ? 'btn-secondary opacity-50 cursor-not-allowed'
                                      : 'btn-light-danger btn-outlined border border-1 border-danger'
                                      }`}
                                    onClick={() => {
                                      if (
                                        rec.status === 'SIGNED_OFF' ||
                                        rec.status === 'PLANNED_SIGN_OFF'
                                      )
                                        return // no-op
                                      setRecordToSignOff(rec)
                                      setSignOffRecordId(rec.id)
                                      setIsSignOffModalOpen(true)
                                    }}
                                    onMouseEnter={() =>
                                      rec.status !== 'SIGNED_OFF' && setHoveredSignOffId(rec.id)
                                    }
                                    onMouseLeave={() => setHoveredSignOffId(null)}
                                    disabled={
                                      rec.status === 'SIGNED_OFF' || rec.status === 'PLANNED_SIGN_OFF'
                                    }
                                  >
                                    <KTSVG
                                      path={
                                        rec.status === 'SIGNED_OFF' ||
                                          rec.status === 'PLANNED_SIGN_OFF'
                                          ? '/media/map/signoff-grey.svg' // a greyed-out icon if you have one
                                          : hoveredSignOffId === rec.id
                                            ? '/media/map/signoff-white.svg'
                                            : '/media/map/signoff-red.svg'
                                      }
                                    />
                                    {/* SIGN OFF */}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    {/* Pagination */}
                    <div className='pagination-wrapper d-flex justify-content-between align-items-center'>
                      <div>
                        Rows per page
                        <SearchableSelect
                          className='form-select d-inline-block w-auto ms-2'
                          style={{ borderRadius: '20px' }}
                          value={rowsPerPage}
                          onChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value))
                            setCurrentPage(1) // Reset to page 1
                          }}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </SearchableSelect>
                      </div>

                      <nav>
                        <ul className='pagination'>
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className='page-link'
                              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            >
                              ‹
                            </button>
                          </li>
                          {Array.from({ length: totalPages }, (_, i) => (
                            <li
                              key={i}
                              className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}
                            >
                              <button className='page-link' onClick={() => setCurrentPage(i + 1)}>
                                {i + 1}
                              </button>
                            </li>
                          ))}
                          <li
                            className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}
                          >
                            <button
                              className='page-link'
                              onClick={() =>
                                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                              }
                            >
                              ›
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {isSignOnModalOpen && (
          <SignOnModal
            isOpen={isSignOnModalOpen}
            onClose={() => {
              setIsSignOnModalOpen(false)
            }}
            onSignedOn={(data) => {
              //1)referesh the list
              handleSignOn(data)
              // 2) stash + show the login link
              setSignOnLink(data.loginLink)
              setShowSignOnLink(true)
            }}
          />
        )}
        {isSignOffModalOpen && signOffRecordId != null && (
          <SignOffModal
            isOpen={isSignOffModalOpen}
            recordId={signOffRecordId}
            signOnDate={recordToSignOff!.signOnDate}
            onClose={() => setIsSignOffModalOpen(false)}
            onSignedOff={(updated) => {
              // update your `records` state in-place, or simply re-fetch:
              getSignOnOffRecords()
                .then(setRecords)
                .catch((err) => {
                  console.error(err)
                  showErrorToast(err, 'Failed to refresh assignments after sign off.', 'load')
                })
              setIsSignOffModalOpen(false)
            }}
          />
        )}

        {/* View Assignment Modal */}
        {isViewAssignmentModalOpen && selectedRecord && (
          <ViewAssignmentModal
            isOpen={isViewAssignmentModalOpen}
            recordData={selectedRecord}
            rankMap={rankMap}
            onClose={() => setIsViewAssignmentModalOpen(false)}
          />
        )}
        {isEditAssignmentModalOpen && selectedRecord && (
          <EditAssignmentModal
            isOpen={isEditAssignmentModalOpen}
            onClose={() => setIsEditAssignmentModalOpen(false)}
            recordData={selectedRecord}
            onUpdate={handleUpdate}
          />
        )}

        {isHistoryModalOpen && historyRecordId != null && (
          <HistoryModal
            isOpen={isHistoryModalOpen}
            recordId={historyRecordId}
            crewName={historyCrewName}
            onClose={() => setIsHistoryModalOpen(false)}
          />
        )}

        {/* {showSignOnLink && signOnLink && (
          <div className='modal-overlay' onClick={() => setShowSignOnLink(false)}>
            <div
              className='modal-content'
              onClick={(e) => e.stopPropagation()}
            //   style={{ maxWidth: '30rem' }}
            >
              <div className='custom-modal-header d-flex justify-content-between'>
                <h5 className='modal-title'>Login Link</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => setShowSignOnLink(false)}
                />
              </div>
              <div className='custom-modal-body'>
                <p>
                  <strong>Use this link to login:</strong>
                </p>
                <a href={signOnLink} target='_blank' rel='noopener noreferrer'>
                  {signOnLink}
                </a>

                <button
                  className='btn btn-light mb-3'
                  onClick={() => {
                    navigator.clipboard.writeText(signOnLink)
                    toast.success('Login link copied to clipboard')
                  }}
                >
                  Copy Link
                </button>
              </div>
            </div>
          </div>
        )} */}

        {showIMOExportModal && (
          <div
            className='modal fade show d-block'
            tabIndex={-1}
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={() => setShowIMOExportModal(false)}
          >
            <div
              className='modal-dialog modal-dialog-centered modal-sm'
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '400px' }}
            >
              <div className='modal-content'>
                <div className='modal-header py-2'>
                  <h6 className='modal-title mb-0'>Export IMO Format</h6>
                  <button
                    type='button'
                    className='btn-close'
                    onClick={() => setShowIMOExportModal(false)}
                  />
                </div>
                <div className='modal-body text-center py-3'>
                  <p className='mb-3' style={{ fontSize: '14px' }}>
                    The records of <strong>{selectedVessel}</strong> will be exported.
                  </p>
                  <p className='text-muted mb-0' style={{ fontSize: '13px' }}>
                    Total records to be exported: <strong>{vesselRecords.length}</strong>
                  </p>
                </div>
                <div className='modal-footer justify-content-center py-2'>
                  <button
                    type='button'
                    className='btn btn-secondary btn-sm me-2'
                    onClick={() => setShowIMOExportModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type='button'
                    className='btn btn-primary btn-sm'
                    onClick={() => {
                      setShowIMOExportModal(false)
                      handleExportIMO()
                    }}
                  >
                    Download
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showVesselFilterAlert && (
          <div
            className='modal fade show d-block'
            tabIndex={-1}
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={() => setShowVesselFilterAlert(false)}
          >
            <div
              className='modal-dialog modal-dialog-centered modal-sm'
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '400px' }}
            >
              <div className='modal-content'>
                <div className='modal-header py-2'>
                  <h6 className='modal-title mb-0'>Select Vessel</h6>
                  <button
                    type='button'
                    className='btn-close'
                    onClick={() => setShowVesselFilterAlert(false)}
                  />
                </div>
                <div className='modal-body text-center py-3'>
                  <p className='mb-3' style={{ fontSize: '14px' }}>
                    Please select a specific vessel in the top filter before exporting IMO format.
                  </p>
                  <p className='text-muted mb-0' style={{ fontSize: '13px' }}>
                    Currently showing: <strong>{isSpecificVesselSelected ? selectedVessel : 'All Vessels'}</strong>
                  </p>
                </div>
                <div className='modal-footer justify-content-center py-2'>
                  <button
                    type='button'
                    className='btn btn-primary btn-sm'
                    onClick={() => setShowVesselFilterAlert(false)}
                  >
                    Okay
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          id='imo-pdf-content-assignment'
          style={{
            display: 'none',
            zIndex: '-99',
            width: '750px',
            padding: '15px',
            boxSizing: 'border-box',
            backgroundColor: '#fff',
          }}
        >
          <style>
            {`
            @page { size: A4 portrait; margin: 15px; }
            #imo-pdf-content-assignment { margin: 0; padding: 0; font-family: serif; font-size: 10pt; height: auto; min-height: auto; }
            #imo-pdf-content-assignment .imo-table { width: 100%; border-collapse: collapse; font-size: 8.5pt; table-layout: fixed; page-break-inside: auto; }
            #imo-pdf-content-assignment .imo-table th,
            #imo-pdf-content-assignment .imo-table td { border: 1px solid black; padding: 4px 6px; vertical-align: top; text-align: left; white-space: normal; word-break: break-word; overflow-wrap: anywhere; line-height: 1.3; overflow: hidden; }
            #imo-pdf-content-assignment .imo-table th { font-weight: bold; background-color: #f2f2f2; text-align: center; font-size: 7.5pt; line-height: 1.15; }
            #imo-pdf-content-assignment .imo-table thead { page-break-inside: avoid; page-break-after: avoid; }
            #imo-pdf-content-assignment .imo-table tbody tr { page-break-inside: avoid; page-break-after: auto; }
            #imo-pdf-content-assignment .imo-table tbody td { height: 35px; }
            #imo-pdf-content-assignment .text-center-val { text-align: center; }
            #imo-pdf-content-assignment .imo-form-title { text-align: center; font-size: 14pt; font-weight: bold; margin-bottom: 2px; }
            #imo-pdf-content-assignment .imo-form-subtitle { text-align: center; font-size: 10pt; margin-top: 0; margin-bottom: 10px; }
            #imo-pdf-content-assignment .signature-box { border: 1px solid black; padding: 5px; min-height: 40px; margin-top: 15px; font-size: 9pt; page-break-inside: avoid !important; break-inside: avoid !important; }
            #imo-pdf-content-assignment .imo-table .top-header-cell { vertical-align: top; padding-top: 10px; }
            #imo-pdf-content-assignment .imo-table .checkbox-label { display: flex; align-items: center; gap: 3px; margin-bottom: 5px; }
            `}
          </style>

          <div style={{ fontSize: '10pt', fontWeight: 'bold', marginBottom: '15px', textAlign: 'left' }}>
            Report Generated:{' '}
            {(() => {
              const now = new Date()
              const dd = String(now.getDate()).padStart(2, '0')
              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
              const mmm = months[now.getMonth()]
              const yyyy = now.getFullYear()
              const hh = String(now.getHours()).padStart(2, '0')
              const mm = String(now.getMinutes()).padStart(2, '0')
              return `${dd}/${mmm}/${yyyy} ${hh}:${mm}`
            })()}
          </div>

          <table className='imo-table'>
            <colgroup>
              <col style={{ width: '4%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '8%' }} />
            </colgroup>
            <thead>
              <tr>
                <th colSpan={12} className='imo-form-title'>CREW LIST</th>
              </tr>
              <tr>
                <th colSpan={12} className='imo-form-subtitle'>(IMO FAL Form 5)</th>
              </tr>
              <tr>
                <td colSpan={12} className='top-header-cell'>
                  <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '15px', marginBottom: '5px' }}>
                    <label className='checkbox-label'>
                      <input type='checkbox' className='form-checkbox' style={{ verticalAlign: 'middle' }} readOnly />
                      <span>Arrival</span>
                    </label>
                    <label className='checkbox-label'>
                      <input type='checkbox' className='form-checkbox' style={{ verticalAlign: 'middle' }} readOnly />
                      <span>Departure</span>
                    </label>
                  </div>
                </td>
              </tr>
              <tr>
                <th colSpan={3}>1.1 Name of ship</th>
                <th colSpan={3}>1.2 IMO number</th>
                <th colSpan={3}>1.3 Call sign</th>
                <th colSpan={3}>1.4 Voyage number</th>
              </tr>
              <tr>
                <td colSpan={3}>{normalizeText(selectedVessel)}</td>
                <td colSpan={3}>{normalizeText(selectedVesselCrew?.imoNumber || selectedVesselRecord?.imoNumber)}</td>
                <td colSpan={3}>{normalizeText(selectedVesselCrew?.callSign || selectedVesselRecord?.call_sign)}</td>
                <td colSpan={3}>{normalizeText(selectedVesselCrew?.voyageNumber)}</td>
              </tr>
              <tr>
                <th colSpan={4}>2. Port of arrival/departure</th>
                <th colSpan={3}>3. Date of arrival/departure</th>
                <th colSpan={3}>4. Flag State of ship</th>
                <th colSpan={2}>5. Last port of call</th>
              </tr>
              <tr>
                <td colSpan={4}>{normalizeText(selectedVesselCrew?.portOfDeparture)}</td>
                <td colSpan={3}>
                  {selectedVesselCrew?.dateOfDeparture ? formatDate(selectedVesselCrew.dateOfDeparture) : ''}
                </td>
                <td colSpan={3}>{normalizeText(selectedVesselCrew?.flagState || selectedVesselRecord?.flag)}</td>
                <td colSpan={2}>{normalizeText(selectedVesselCrew?.portOfArrival)}</td>
              </tr>
              <tr>
                <th>6. No.</th>
                <th>7. Family name</th>
                <th>8. Given names</th>
                <th>9. Rank or rating</th>
                <th>10. Nationality</th>
                <th>11. Date of birth</th>
                <th>12. Place of birth</th>
                <th>14. Nature of identity document</th>
                <th>15. Number of identity document</th>
                <th>16. Issuing State of identity document</th>
                <th>17. Expiry date of identity document</th>
              </tr>
            </thead>
            <tbody>
              {vesselRecords.length > 0 ? (
                vesselRecords.map((rec, index) => {
                  const crewRow = resolveCrewForRecord(rec)
                  const familyName = normalizeText(crewRow?.familyName)
                  const givenNames = normalizeText(crewRow?.name || crewRow?.givenNames)
                  return (
                    <tr key={rec.id || index}>
                      <td className='text-center-val'>{index + 1}</td>
                      <td>{familyName}</td>
                      <td>{givenNames}</td>
                      <td>{rec.rankInfo?.rank || rankMap[rec.rank] || ''}</td>
                      <td>{normalizeText(crewRow?.nationality)}</td>
                      <td>{formatDate(crewRow?.dateOfBirth)}</td>
                      <td>{normalizeText(crewRow?.placeOfBirth)}</td>
                      <td>{normalizeText(crewRow?.identityDocType)}</td>
                      <td>{normalizeText(crewRow?.identityDocNumber)}</td>
                      <td>{normalizeText(crewRow?.identityIssuingState)}</td>
                      <td>{formatDate(crewRow?.identityExpiryDate)}</td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={12} className='text-center-val'>No crew data to display for IMO form.</td>
                </tr>
              )}
            </tbody>
          </table>

          <div className='signature-box'>
            <p>18. Date and signature by master, authorized agent or officer</p>
          </div>
        </div>

        <ToastContainer
          position='top-center'
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          style={{ top: '5rem' }}
        />
      </div>
    </div>
  )
}

export { AssignmentPage }
